import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError, parseDrfFieldErrors } from "../../../shared/api/drfErrors";
import { fetchCompanyClients } from "../../companyClients/api/companyClients";
import { clientDisplayName } from "../../companyClients/lib/clientLabels";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isActiveMembership,
  isAdminMembership,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
  sameMembershipGeneration,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";
import ScopeAwarePropertyPicker, {
  PROPERTY_SCOPE_COMPANY,
} from "../../companyProperties/widgets/ScopeAwarePropertyPicker";
import {
  assignCompanyTask,
  assignCompanyViewing,
  deleteCompanyTask,
  deleteCompanyViewing,
  fetchCompanyTask,
  fetchCompanyViewing,
  patchCompanyTask,
  patchCompanyViewing,
  unassignCompanyTask,
  unassignCompanyViewing,
} from "../api/companyWork";
import {
  executorLabel,
  formatDateTime,
  formatOutcomeHistoryEntry,
  isCorporateWorkWritesAvailable,
  isLinkedAccessible,
  linkedClientLabel,
  linkedPropertyLabel,
  memberDisplayName,
  membershipGenerationOf,
  messageFromWorkError,
  outcomeHistoryEntries,
  priorityLabel,
  TASK_PRIORITY_LABELS,
  TASK_STATUS_OPTIONS,
  TASK_TERMINAL_STATUSES,
  taskStatusLabel,
  VIEWING_STATUS_OPTIONS,
  VIEWING_TERMINAL_STATUSES,
  viewingStatusLabel,
} from "../lib/workLabels";
import CompanyWorkCreateModal from "./CompanyWorkCreateModal";
import CompanyUkrainianDateField from "./CompanyUkrainianDateField";
import {
  isoFromLocalDateTimeString,
  localDateTimeStringFromIso,
  parseErrorMessage,
} from "../lib/ukrainianDateEditor";

function unwrapMembers(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function unwrapResults(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function toLocalInputValue(isoOrEmpty) {
  return localDateTimeStringFromIso(isoOrEmpty);
}

function fromLocalInputValue(local) {
  if (!local) return null;
  return isoFromLocalDateTimeString(local);
}

/**
 * Shared corporate Task / Viewing detail + lifecycle.
 * kind: "task" | "viewing"
 */
function CompanyWorkDetailContent({ kind }) {
  const params = useParams();
  const companyId = params.companyId;
  const workId = kind === "viewing" ? params.viewingId : params.taskId;
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const relinkClientsChannelRef = useRef(0);
  const stateRef = useRef("loading");
  const draftDirtyRef = useRef(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [work, setWork] = useState(null);
  const [state, setState] = useState("loading");
  const [mutationBusy, setMutationBusy] = useState(false);
  const [writesSuspended, setWritesSuspended] = useState(false);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;

  const markDraftDirty = () => {
    draftDirtyRef.current = true;
  };
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [resultDraft, setResultDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [priorityDraft, setPriorityDraft] = useState("medium");
  const [dueLocal, setDueLocal] = useState("");
  const [scheduledLocal, setScheduledLocal] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  const [assignUserId, setAssignUserId] = useState("");
  const [members, setMembers] = useState([]);
  const [relinkClientId, setRelinkClientId] = useState(null);
  const [relinkPropertyId, setRelinkPropertyId] = useState(null);
  const [relinkClientOptions, setRelinkClientOptions] = useState([]);
  const [relinkClientQuery, setRelinkClientQuery] = useState("");
  const [nextTaskOpen, setNextTaskOpen] = useState(false);
  const [nextTaskDenied, setNextTaskDenied] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const dueFieldRef = useRef(null);
  const scheduledFieldRef = useRef(null);

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const writesAvailable =
    authorityMatches && isCorporateWorkWritesAvailable(company);
  const caps = work?.capabilities || {};
  const generation = membershipGenerationOf(membership);
  const isOa = isAdminMembership(company, membership);
  const resourceId = `${kind}:${workId}`;

  const applyWork = (row) => {
    setWork(row);
    setResultDraft(row?.result_summary || "");
    setStatusDraft(row?.status || "");
    setTitleDraft(row?.title || "");
    setDescriptionDraft(row?.description || "");
    setPriorityDraft(row?.priority || "medium");
    setDueLocal(toLocalInputValue(row?.due_date));
    setScheduledLocal(toLocalInputValue(row?.scheduled_at));
    setNotesDraft(row?.notes || "");
    setAssignUserId(
      row?.responsible_user_id != null
        ? String(row.responsible_user_id)
        : "",
    );
    setRelinkClientId(
      isLinkedAccessible(row?.linked_client) ? row.linked_client.id : null,
    );
    setRelinkPropertyId(
      isLinkedAccessible(row?.linked_property) ? row.linked_property.id : null,
    );
  };

  const clearProtected = useCallback(() => {
    relinkClientsChannelRef.current += 1;
    setWork(null);
    setMembers([]);
    setResultDraft("");
    setStatusDraft("");
    setTitleDraft("");
    setDescriptionDraft("");
    setPriorityDraft("medium");
    setDueLocal("");
    setScheduledLocal("");
    setNotesDraft("");
    setAssignUserId("");
    setRelinkClientId(null);
    setRelinkPropertyId(null);
    setRelinkClientOptions([]);
    setRelinkClientQuery("");
    setNextTaskOpen(false);
    setNextTaskDenied(null);
    setConfirmDelete(false);
    setActionError(null);
    setFieldErrors({});
  }, []);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId || !workId) {
      setState("not-found");
      return;
    }
    const keepVisible = background && stateRef.current === "ready";
    let op = null;
    // Phase 1 — authority discovery against current live scope.
    op = scopeGate.beginRead({
      companyId,
      actorId,
      resourceId,
    });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const mineMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId)
          ? mine.membership
          : null;
      const nextGeneration = membershipGenerationOf(mineMatches);
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);

      // Bind resolved generation, then capture a fresh work read.
      scopeGate.bindIdentity({
        companyId,
        actorId,
        membershipGeneration: nextGeneration,
        resourceId,
        bindMembershipGeneration: true,
        bindResourceId: true,
      });

      if (!isActiveMembership(companyData, mineMatches)) {
        clearProtected();
        setState("denied");
        return;
      }

      op = scopeGate.beginRead({
        companyId,
        actorId,
        membershipGeneration: nextGeneration,
        resourceId,
      });
      const row =
        kind === "viewing"
          ? await fetchCompanyViewing(companyId, workId)
          : await fetchCompanyTask(companyId, workId);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (!(keepVisible && draftDirtyRef.current)) {
        applyWork(row);
        draftDirtyRef.current = false;
      }
      if (isAdminMembership(companyData, mineMatches)) {
        try {
          const memberData = await fetchMembers(companyId, "active");
          if (scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
            setMembers(unwrapMembers(memberData));
          }
        } catch {
          /* ignore member load for read path */
        }
      }
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
    } catch (err) {
      if (
        op &&
        !scopeGate.readStillValid(op, { mounted: mountedRef.current })
      ) {
        return;
      }
      if (!mountedRef.current) return;
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      if (status === 403 || status === 404) {
        clearProtected();
        setState("denied");
        return;
      }
      clearProtected();
      setError(messageFromError(err));
      setState("error");
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [actorId, clearProtected, companyId, kind, resourceId, scopeGate, workId]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({
      companyId,
      actorId,
      resourceId,
      bindResourceId: true,
    });
    clearProtected();
    load();
    return () => {
      mountedRef.current = false;
      relinkClientsChannelRef.current += 1;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId, kind, workId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: mutationBusy,
    onRevalidate: () => load({ background: true }),
  });

  const loadRelinkClients = useCallback(async () => {
    if (!companyId || !isOa) return;
    // Local channel + lifetime only — do not beginRead (would share
    // readGeneration with the main detail/authority load).
    const channel = ++relinkClientsChannelRef.current;
    const lifetimeSnap = scopeGate.currentLifetime();
    const expectedCompany = companyId;
    const expectedActor = actorId;
    const expectedResource = resourceId;
    const expectedGeneration = generation;
    try {
      const data = await fetchCompanyClients(companyId, {
        search: relinkClientQuery.trim() || undefined,
        page_size: 20,
      });
      if (channel !== relinkClientsChannelRef.current) return;
      if (
        !scopeGate.lifetimeStillValid(
          { lifetime: lifetimeSnap },
          { mounted: mountedRef.current },
        )
      ) {
        return;
      }
      const bound = scopeGate.getBoundIdentity();
      if (!sameCompanyId(bound.companyId, expectedCompany)) return;
      if (!sameActorId(bound.actorId, expectedActor)) return;
      if (
        bound.resourceIdBound &&
        bound.resourceId != null &&
        String(bound.resourceId) !== String(expectedResource)
      ) {
        return;
      }
      if (
        bound.membershipGenerationBound &&
        expectedGeneration != null &&
        !sameMembershipGeneration(
          bound.membershipGeneration,
          expectedGeneration,
        )
      ) {
        return;
      }
      setRelinkClientOptions(unwrapResults(data));
    } catch {
      // Stale rejected requests must not clear fresher options.
      if (channel !== relinkClientsChannelRef.current) return;
      if (
        !scopeGate.lifetimeStillValid(
          { lifetime: lifetimeSnap },
          { mounted: mountedRef.current },
        )
      ) {
        return;
      }
      setRelinkClientOptions([]);
    }
  }, [
    actorId,
    companyId,
    generation,
    isOa,
    relinkClientQuery,
    resourceId,
    scopeGate,
  ]);

  useEffect(() => {
    if (state === "ready" && writesAvailable && isOa && caps.can_relink) {
      loadRelinkClients();
    }
  }, [state, writesAvailable, isOa, caps.can_relink, loadRelinkClients]);

  const runMutation = async (fn, { afterSuccess } = {}) => {
    if (!writesAvailable || generation == null || busyOpRef.current || writesSuspendedRef.current) {
      return;
    }
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      membershipGeneration: generation,
      resourceId,
    });
    busyOpRef.current = op;
    setMutationBusy(true);
    setBusy(true);
    setActionError(null);
    try {
      const result = await fn();
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      await load();
      if (!scopeGate.lifetimeStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (typeof afterSuccess === "function") {
        afterSuccess(result);
      }
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (err?.response?.status === 409) {
        setActionError(messageFromWorkError(err));
        await load();
        return;
      }
      if (err?.response?.status === 403 || err?.response?.status === 404) {
        setActionError(messageFromWorkError(err));
        await load();
        return;
      }
      if (err?.response?.status === 400) {
        const moderatedKeys =
          kind === "viewing"
            ? ["notes", "result_summary", "scheduled_at", "status", "client_id", "property_id"]
            : [
                "title",
                "description",
                "result_summary",
                "due_date",
                "status",
                "priority",
                "client_id",
                "property_id",
              ];
        const parsed = parseDrfFieldErrors(err, moderatedKeys);
        const { _form, ...fields } = parsed;
        // Map DRF due_date/scheduled_at onto local date-field keys.
        if (fields.due_date && !fields.due) {
          fields.due = fields.due_date;
          delete fields.due_date;
        }
        if (fields.scheduled_at && !fields.scheduled) {
          fields.scheduled = fields.scheduled_at;
          delete fields.scheduled_at;
        }
        setFieldErrors((prev) => ({ ...prev, ...fields }));
        setActionError(_form || null);
        return;
      }
      setActionError(messageFromWorkError(err));
    } finally {
      if (busyOpRef.current === op) {
        busyOpRef.current = null;
        setBusy(false);
        setMutationBusy(false);
      }
    }
  };

  const saveOpenFields = () => {
    const nextErrors = {};
    let dueValue = dueLocal;
    let scheduledValue = scheduledLocal;
    if (kind === "task") {
      const dueCommit = dueFieldRef.current?.commit?.();
      if (dueCommit && dueCommit.ok === false) {
        nextErrors.due = dueCommit.error;
      } else if (dueCommit) {
        dueValue = dueCommit.value;
      }
    } else {
      const scheduledCommit = scheduledFieldRef.current?.commit?.();
      if (!scheduledCommit?.ok || !scheduledCommit.value) {
        nextErrors.scheduled =
          scheduledCommit?.error || parseErrorMessage("datetime");
      } else {
        scheduledValue = scheduledCommit.value;
      }
    }
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      if (nextErrors.due) dueFieldRef.current?.focus?.();
      else scheduledFieldRef.current?.focus?.();
      return;
    }
    setFieldErrors({});
    return runMutation(async () => {
      const body = {
        actor_membership_generation: generation,
        assignment_revision: work.assignment_revision,
      };
      if (kind === "task") {
        body.title = titleDraft.trim();
        body.description = descriptionDraft;
        body.priority = priorityDraft;
        body.due_date = fromLocalInputValue(dueValue);
        body.status = statusDraft;
        if (TASK_TERMINAL_STATUSES.has(statusDraft)) {
          body.result_summary = resultDraft.trim();
        }
        return patchCompanyTask(companyId, workId, body);
      }
      body.scheduled_at = fromLocalInputValue(scheduledValue);
      body.notes = notesDraft;
      body.status = statusDraft;
      if (VIEWING_TERMINAL_STATUSES.has(statusDraft)) {
        body.result_summary = resultDraft.trim();
      }
      return patchCompanyViewing(companyId, workId, body);
    });
  };

  const saveRelink = () =>
    runMutation(async () => {
      const body = {
        actor_membership_generation: generation,
        assignment_revision: work.assignment_revision,
        client_id: relinkClientId != null ? Number(relinkClientId) : null,
        property_id:
          relinkPropertyId != null ? Number(relinkPropertyId) : null,
      };
      if (kind === "viewing") {
        if (relinkClientId == null || relinkPropertyId == null) {
          throw new Error("Перегляд потребує клієнта та об'єкта.");
        }
        return patchCompanyViewing(companyId, workId, body);
      }
      return patchCompanyTask(companyId, workId, body);
    });

  const doAssign = () =>
    runMutation(async () => {
      const body = {
        responsible_user_id: Number(assignUserId),
        assignment_revision: work.assignment_revision,
        actor_membership_generation: generation,
      };
      return kind === "viewing"
        ? assignCompanyViewing(companyId, workId, body)
        : assignCompanyTask(companyId, workId, body);
    });

  const doUnassign = () =>
    runMutation(async () => {
      const body = {
        assignment_revision: work.assignment_revision,
        actor_membership_generation: generation,
      };
      return kind === "viewing"
        ? unassignCompanyViewing(companyId, workId, body)
        : unassignCompanyTask(companyId, workId, body);
    });

  const doDelete = () =>
    runMutation(async () => {
      const params = { actor_membership_generation: generation };
      if (kind === "viewing") {
        await deleteCompanyViewing(companyId, workId, params);
      } else {
        await deleteCompanyTask(companyId, workId, params);
      }
      return null;
    }, {
      afterSuccess: () => {
        navigate(`/companies/${companyId}/tasks`);
      },
    });

  const openNextTask = () => {
    // Consume fresh canonical work after reload — not mutation-response projections.
    const current = work;
    if (!current) return;
    const clientOk = isLinkedAccessible(current.linked_client);
    const propertyOk = isLinkedAccessible(current.linked_property);
    if (!clientOk || !propertyOk) {
      setNextTaskDenied(
        "Немає незалежного доступу до повʼязаних записів для створення наступної задачі.",
      );
      setNextTaskOpen(false);
      return;
    }
    setNextTaskDenied(null);
    setNextTaskOpen(true);
  };

  const headerTitle =
    authorityMatches && work
      ? kind === "viewing"
        ? `Перегляд #${workId}`
        : work.title || `Задача #${workId}`
      : kind === "viewing"
        ? "Перегляд"
        : "Задача";

  return (
    <section
      className="clients-page feature-company-workspace feature-company-work"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
      <CompanyPageHeader
        title={headerTitle}
        subtitle="Робота компанії"
        company={authorityMatches ? company : null}
        membership={authorityMatches ? membership : null}
        companyId={companyId}
        activeNav="tasks"
        actions={
          <Link
            to={`/companies/${companyId}/tasks`}
            className="action-btn action-btn--ghost"
          >
            До списку
          </Link>
        }
      />

      {state === "loading" ? <Spinner label="Завантаження" /> : null}
      {state === "error" ? (
        <EmptyState
          title="Помилка"
          description={error}
          primaryAction={
            <button type="button" className="action-btn" onClick={load}>
              Повторити
            </button>
          }
        />
      ) : null}
      {state === "denied" ? (
        <EmptyState
          title="Немає доступу"
          description="Запис недоступний для поточного членства."
        />
      ) : null}

      {state === "ready" && work && authorityMatches ? (
        <div className="feature-company-work__detail">
          {!writesAvailable ? (
            <p className="feature-company-workspace__meta" role="status">
              Режим лише читання.
            </p>
          ) : null}

          <section
            className="feature-company-workspace__card"
            aria-label="Огляд"
          >
          <h3>Огляд</h3>
          <dl className="feature-company-work__meta-grid">
            <div>
              <dt>Статус</dt>
              <dd>
                {kind === "viewing"
                  ? viewingStatusLabel(work.status)
                  : taskStatusLabel(work.status)}
              </dd>
            </div>
            {kind === "task" ? (
              <div>
                <dt>Пріоритет</dt>
                <dd>{priorityLabel(work.priority)}</dd>
              </div>
            ) : null}
            <div>
              <dt>Виконавець</dt>
              <dd>{executorLabel(work.responsible_user_id, members)}</dd>
            </div>
            <div>
              <dt>Клієнт</dt>
              <dd>{linkedClientLabel(work.linked_client)}</dd>
            </div>
            <div>
              <dt>Обʼєкт</dt>
              <dd>{linkedPropertyLabel(work.linked_property)}</dd>
            </div>
            <div>
              <dt>
                {kind === "viewing" ? "Заплановано" : "Дедлайн"}
              </dt>
              <dd>
                {kind === "viewing"
                  ? formatDateTime(work.scheduled_at)
                  : work.due_date
                    ? formatDateTime(work.due_date)
                    : "Без дедлайну"}
              </dd>
            </div>
          </dl>
          </section>

          {kind === "task" && work.description ? (
            <section className="feature-company-workspace__card" aria-label="Опис">
              <h3>Опис</h3>
              <p className="feature-company-work__readable">{work.description}</p>
            </section>
          ) : null}
          {kind === "viewing" && work.notes ? (
            <section className="feature-company-workspace__card" aria-label="Нотатки">
              <h3>Нотатки</h3>
              <p className="feature-company-work__readable">{work.notes}</p>
            </section>
          ) : null}

          <section
            className="feature-company-workspace__card"
            aria-label="Історія результатів"
          >
            <h3>Результат і історія</h3>
            {work.result_summary ? (
              <p className="feature-company-work__readable">
                <strong>Результат: </strong>
                {work.result_summary}
              </p>
            ) : null}
            {outcomeHistoryEntries(work).length === 0 ? (
              <p className="feature-company-workspace__meta">Порожньо</p>
            ) : (
              <ol className="feature-company-work__history">
                {outcomeHistoryEntries(work).map((entry, index) => (
                  <li
                    key={`${entry.at || index}-${entry.kind || ""}-${entry.to_status || ""}`}
                  >
                    {formatOutcomeHistoryEntry(entry, kind)}
                  </li>
                ))}
              </ol>
            )}
          </section>

          {writesAvailable &&
          (caps.can_mutate_open || caps.can_reopen) ? (
            <section className="feature-company-workspace__card">
            <form
              className="ui-form"
              onSubmit={(e) => {
                e.preventDefault();
                saveOpenFields();
              }}
            >
              <h3>Оновити роботу</h3>
              {kind === "task" ? (
                <>
                  <label className="ui-form__field">
                    <span>Назва</span>
                    <TextInput
                      value={titleDraft}
                      onChange={(e) => {
                        markDraftDirty();
                        setTitleDraft(e.target.value);
                        setFieldErrors((prev) => {
                          if (!prev.title) return prev;
                          const next = { ...prev };
                          delete next.title;
                          return next;
                        });
                      }}
                      disabled={busy || writesSuspended || !caps.can_mutate_open}
                      aria-invalid={fieldErrors.title ? "true" : undefined}
                    />
                    {fieldErrors.title ? (
                      <InlineError>{fieldErrors.title}</InlineError>
                    ) : null}
                  </label>
                  <label className="ui-form__field">
                    <span>Опис</span>
                    <Textarea
                      value={descriptionDraft}
                      onChange={(e) => {
                        markDraftDirty();
                        setDescriptionDraft(e.target.value);
                        setFieldErrors((prev) => {
                          if (!prev.description) return prev;
                          const next = { ...prev };
                          delete next.description;
                          return next;
                        });
                      }}
                      disabled={busy || writesSuspended || !caps.can_mutate_open}
                      rows={3}
                      aria-invalid={fieldErrors.description ? "true" : undefined}
                    />
                    {fieldErrors.description ? (
                      <InlineError>{fieldErrors.description}</InlineError>
                    ) : null}
                  </label>
                  <label className="ui-form__field">
                    <span>Пріоритет</span>
                    <Select
                      value={priorityDraft}
                      onChange={(e) => {
                        markDraftDirty();
                        setPriorityDraft(e.target.value);
                      }}
                      disabled={busy || writesSuspended || !caps.can_mutate_open}
                      options={Object.entries(TASK_PRIORITY_LABELS).map(
                        ([value, label]) => ({ value, label }),
                      )}
                    />
                  </label>
                  <CompanyUkrainianDateField
                    ref={dueFieldRef}
                    id="company-work-detail-due"
                    label="Дедлайн"
                    mode="datetime"
                    canonicalValue={dueLocal}
                    onCanonicalChange={(value) => {
                      markDraftDirty();
                      setDueLocal(value);
                      setFieldErrors((prev) => {
                        if (!prev.due) return prev;
                        const next = { ...prev };
                        delete next.due;
                        return next;
                      });
                    }}
                    error={fieldErrors.due}
                    disabled={busy || writesSuspended || !caps.can_mutate_open}
                  />
                </>
              ) : (
                <>
                  <CompanyUkrainianDateField
                    ref={scheduledFieldRef}
                    id="company-work-detail-scheduled"
                    label="Дата і час"
                    required
                    mode="datetime"
                    canonicalValue={scheduledLocal}
                    onCanonicalChange={(value) => {
                      markDraftDirty();
                      setScheduledLocal(value);
                      setFieldErrors((prev) => {
                        if (!prev.scheduled) return prev;
                        const next = { ...prev };
                        delete next.scheduled;
                        return next;
                      });
                    }}
                    error={fieldErrors.scheduled}
                    disabled={busy || writesSuspended || !caps.can_mutate_open}
                  />
                  <label className="ui-form__field">
                    <span>Нотатки</span>
                    <Textarea
                      value={notesDraft}
                      onChange={(e) => {
                        markDraftDirty();
                        setNotesDraft(e.target.value);
                        setFieldErrors((prev) => {
                          if (!prev.notes) return prev;
                          const next = { ...prev };
                          delete next.notes;
                          return next;
                        });
                      }}
                      disabled={busy || writesSuspended || !caps.can_mutate_open}
                      rows={3}
                      aria-invalid={fieldErrors.notes ? "true" : undefined}
                    />
                    {fieldErrors.notes ? (
                      <InlineError>{fieldErrors.notes}</InlineError>
                    ) : null}
                  </label>
                </>
              )}
              <label className="ui-form__field">
                <span>Статус</span>
                <Select
                  value={statusDraft}
                  onChange={(e) => {
                    markDraftDirty();
                    setStatusDraft(e.target.value);
                  }}
                  disabled={
                    busy ||
                    writesSuspended ||
                    !(
                      caps.can_mutate_open ||
                      (caps.can_reopen &&
                        (TASK_TERMINAL_STATUSES.has(work.status) ||
                          VIEWING_TERMINAL_STATUSES.has(work.status)))
                    )
                  }
                  options={
                    kind === "viewing"
                      ? VIEWING_STATUS_OPTIONS
                      : TASK_STATUS_OPTIONS
                  }
                />
              </label>
              {(kind === "task"
                ? TASK_TERMINAL_STATUSES.has(statusDraft)
                : VIEWING_TERMINAL_STATUSES.has(statusDraft)) ? (
                <label className="ui-form__field">
                  <span>Результат / причина (обовʼязково)</span>
                  <Textarea
                    value={resultDraft}
                    onChange={(e) => {
                      markDraftDirty();
                      setResultDraft(e.target.value);
                      setFieldErrors((prev) => {
                        if (!prev.result_summary) return prev;
                        const next = { ...prev };
                        delete next.result_summary;
                        return next;
                      });
                    }}
                    maxLength={2000}
                    disabled={busy || writesSuspended}
                    rows={3}
                    required
                    aria-invalid={fieldErrors.result_summary ? "true" : undefined}
                  />
                  {fieldErrors.result_summary ? (
                    <InlineError>{fieldErrors.result_summary}</InlineError>
                  ) : null}
                </label>
              ) : null}
              <button
                type="submit"
                className="ui-form__button ui-form__button--primary"
                disabled={busy || writesSuspended}
              >
                {busy ? <Spinner size="sm" label="Збереження" /> : "Зберегти"}
              </button>
            </form>
            </section>
          ) : null}

          {writesAvailable && isOa && caps.can_relink ? (
            <section
              aria-label="Звʼязки"
              className="feature-company-workspace__card"
            >
              <h3>Звʼязки</h3>
              <div className="feature-company-work__links">
                <div className="feature-company-work__links-group">
                  <span>Клієнт</span>
                  <div className="feature-client-detail__property-search">
                    <TextInput
                      aria-label="Пошук клієнта"
                      value={relinkClientQuery}
                      onChange={(e) => setRelinkClientQuery(e.target.value)}
                      disabled={busy || writesSuspended}
                    />
                    <button
                      type="button"
                      className="ui-form__button ui-form__button--ghost"
                      onClick={loadRelinkClients}
                      disabled={busy || writesSuspended}
                    >
                      Шукати
                    </button>
                  </div>
                  <ul
                    className="feature-client-detail__property-options"
                    role="listbox"
                  >
                    {kind === "task" ? (
                      <li>
                        <button
                          type="button"
                          role="option"
                          aria-selected={relinkClientId == null}
                          className={[
                            "feature-client-detail__property-option",
                            relinkClientId == null
                              ? "feature-client-detail__property-option--selected"
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          disabled={busy || writesSuspended}
                          onClick={() => {
                            markDraftDirty();
                            setRelinkClientId(null);
                          }}
                        >
                          Без клієнта
                        </button>
                      </li>
                    ) : null}
                    {relinkClientOptions.map((row) => (
                      <li key={row.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={
                            relinkClientId != null &&
                            String(relinkClientId) === String(row.id)
                          }
                          className={[
                            "feature-client-detail__property-option",
                            relinkClientId != null &&
                            String(relinkClientId) === String(row.id)
                              ? "feature-client-detail__property-option--selected"
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          disabled={busy || writesSuspended}
                          onClick={() => {
                            markDraftDirty();
                            setRelinkClientId(row.id);
                          }}
                        >
                          {clientDisplayName(row)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="feature-company-work__links-group">
                  <span>Обʼєкт</span>
                  <ScopeAwarePropertyPicker
                    scope={PROPERTY_SCOPE_COMPANY}
                    companyId={companyId}
                    actorId={actorId}
                    membershipGeneration={generation}
                    value={relinkPropertyId}
                    onChange={(id) => {
                      markDraftDirty();
                      setRelinkPropertyId(id);
                    }}
                    disabled={busy || writesSuspended}
                    label="Об'єкт нерухомості компанії"
                  />
                  {kind === "task" ? (
                    <button
                      type="button"
                      className="ui-form__button ui-form__button--ghost"
                      disabled={busy || relinkPropertyId == null}
                      onClick={() => {
                        markDraftDirty();
                        setRelinkPropertyId(null);
                      }}
                    >
                      Без обʼєкта
                    </button>
                  ) : null}
                </div>
                <div className="feature-company-work__links-actions">
                  <button
                    type="button"
                    className="ui-form__button ui-form__button--primary"
                    disabled={busy || writesSuspended}
                    onClick={saveRelink}
                  >
                    Зберегти звʼязки
                  </button>
                </div>
              </div>
            </section>
          ) : null}

          {writesAvailable && isOa && caps.can_assign ? (
            <section
              className="feature-company-workspace__card"
              aria-label="Призначення"
            >
            <h3>Призначення</h3>
            <div className="feature-company-work__assign-row">
              <label className="ui-form__field">
                <span>Призначити виконавця</span>
                <Select
                  value={assignUserId}
                  onChange={(e) => {
                    markDraftDirty();
                    setAssignUserId(e.target.value);
                  }}
                  disabled={busy || writesSuspended}
                  placeholder="—"
                  options={members
                    .map((m) => {
                      const userId = m.user?.id ?? m.user_id;
                      if (userId == null) return null;
                      return {
                        value: String(userId),
                        label: memberDisplayName(m) || `Учасник #${userId}`,
                      };
                    })
                    .filter(Boolean)}
                />
              </label>
              <button
                type="button"
                className="ui-form__button ui-form__button--primary"
                disabled={busy || !assignUserId}
                onClick={doAssign}
              >
                Призначити
              </button>
              <button
                type="button"
                className="ui-form__button ui-form__button--ghost"
                disabled={busy || work.responsible_user_id == null}
                onClick={doUnassign}
              >
                Зняти призначення
              </button>
            </div>
            </section>
          ) : null}

          {writesAvailable && caps.can_delete ? (
            <section
              className="feature-company-work__delete"
              aria-label="Видалення"
            >
            <h3>Видалення</h3>
            <div className="feature-company-workspace__inline-actions">
              {!confirmDelete ? (
                <button
                  type="button"
                  className="action-btn action-btn--danger"
                  disabled={busy || writesSuspended}
                  onClick={() => setConfirmDelete(true)}
                >
                  Видалити
                </button>
              ) : (
                <>
                  <p role="alert">Підтвердіть видалення.</p>
                  <button
                    type="button"
                    className="action-btn action-btn--danger"
                    disabled={busy || writesSuspended}
                    onClick={doDelete}
                  >
                    Підтвердити видалення
                  </button>
                  <button
                    type="button"
                    className="action-btn action-btn--ghost"
                    disabled={busy || writesSuspended}
                    onClick={() => setConfirmDelete(false)}
                  >
                    Скасувати
                  </button>
                </>
              )}
            </div>
            </section>
          ) : null}

          {actionError ? (
            <p className="feature-company-workspace__error" role="alert">
              {actionError}
            </p>
          ) : null}

          {nextTaskDenied ? (
            <p className="feature-company-workspace__meta" role="status">
              {nextTaskDenied}
            </p>
          ) : null}

          {kind === "viewing" &&
          VIEWING_TERMINAL_STATUSES.has(work.status) &&
          writesAvailable ? (
            <div className="feature-company-workspace__card feature-company-workspace__inline-actions">
              <button
                type="button"
                className="action-btn"
                disabled={busy || writesSuspended}
                onClick={openNextTask}
              >
                Створити наступну задачу
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <CompanyWorkCreateModal
        open={nextTaskOpen && authorityMatches && writesAvailable}
        kind="task"
        companyId={companyId}
        company={company}
        membership={membership}
        actorId={actorId}
        initialClientId={
          isLinkedAccessible(work?.linked_client) ? work.linked_client.id : null
        }
        initialPropertyId={
          isLinkedAccessible(work?.linked_property)
            ? work.linked_property.id
            : null
        }
        onClose={() => setNextTaskOpen(false)}
        onCreated={(created) => {
          setNextTaskOpen(false);
          navigate(`/companies/${companyId}/tasks/${created.id}`);
        }}
      />
      </div>
    </section>
  );
}

export default CompanyWorkDetailContent;
